/**
 * 式言語（D-0009）— サンドボックス評価器＋パーサ
 * -----------------------------------------------------------------------------
 * ・eval / new Function / 動的コードは一切使わない。テキスト式を独自にトークナイズ→
 *   AST（schema/app-definition.ts の Expression と同形）→ ホワイトリスト関数のみで評価。
 * ・深さ/ステップ上限つき（式 DoS 防止）。列参照は現在行＋context のみ（N+1 回避 / D-0009）。
 * ・構文（AppSheet/Excel 風）:
 *     リテラル: 123 / 1.5 / "文字列"(""でエスケープ) / TRUE / FALSE
 *     列参照:   [列名]
 *     演算子:   + - * / %  比較 = <> == != > >= < <=  連結 &  単項 -
 *     関数:     IF(c,a,b) AND(...) OR(...) NOT(x) CONCATENATE(...) UPPER LOWER LEN TRIM
 *               CONTAINS(s,sub) ISBLANK(x) COALESCE(...) TODAY() NOW() USER()
 *               DATEDIFF(a,b) ROUND(n[,d]) ABS MIN(...) MAX(...)
 */

const MAX_STEPS = 10000;
const MAX_DEPTH = 64;

// ---------------------------------------------------------------- tokenizer
const OPS3 = [];
const OPS2 = ["<>", "<=", ">=", "==", "!="];
const OPS1 = ["+", "-", "*", "/", "%", "&", "=", "<", ">", "(", ")", ","];

function tokenize(src) {
  const toks = [];
  let i = 0;
  const isDigit = (c) => c >= "0" && c <= "9";
  const isIdentStart = (c) => /[A-Za-z_]/.test(c);
  const isIdent = (c) => /[A-Za-z0-9_.]/.test(c);
  while (i < src.length) {
    const c = src[i];
    if (c === " " || c === "\t" || c === "\n" || c === "\r") { i++; continue; }
    if (c === '"') {
      let s = ""; i++;
      while (i < src.length) {
        if (src[i] === '"') {
          if (src[i + 1] === '"') { s += '"'; i += 2; continue; }
          i++; break;
        }
        s += src[i++];
      }
      toks.push({ t: "str", v: s });
      continue;
    }
    if (c === "[") {
      let s = ""; i++;
      while (i < src.length && src[i] !== "]") s += src[i++];
      if (src[i] !== "]") throw new Error("列参照 [ が閉じていません");
      i++;
      toks.push({ t: "col", v: s.trim() });
      continue;
    }
    if (isDigit(c) || (c === "." && isDigit(src[i + 1]))) {
      let s = "";
      while (i < src.length && (isDigit(src[i]) || src[i] === ".")) s += src[i++];
      toks.push({ t: "num", v: Number(s) });
      continue;
    }
    if (isIdentStart(c)) {
      let s = "";
      while (i < src.length && isIdent(src[i])) s += src[i++];
      const up = s.toUpperCase();
      if (up === "TRUE") toks.push({ t: "bool", v: true });
      else if (up === "FALSE") toks.push({ t: "bool", v: false });
      else toks.push({ t: "ident", v: s });
      continue;
    }
    const two = src.slice(i, i + 2);
    if (OPS2.includes(two)) { toks.push({ t: "op", v: two === "<>" ? "!=" : two === "==" ? "==" : two }); i += 2; continue; }
    if (OPS1.includes(c)) { toks.push({ t: "op", v: c === "=" ? "==" : c }); i++; continue; }
    throw new Error(`不明な文字: ${c}`);
  }
  toks.push({ t: "eof" });
  return toks;
}

// ---------------------------------------------------------------- parser (recursive descent)
export function parseFormula(src) {
  const toks = tokenize(src);
  let p = 0;
  const peek = () => toks[p];
  const next = () => toks[p++];
  const expect = (v) => { const tk = next(); if (tk.t !== "op" || tk.v !== v) throw new Error(`'${v}' が必要です`); };

  // 優先順位: comparison < concat(&) < add < mul < unary < primary
  function parseExpr() { return parseComparison(); }

  function parseComparison() {
    let left = parseConcat();
    const cmp = ["==", "!=", ">", ">=", "<", "<="];
    while (peek().t === "op" && cmp.includes(peek().v)) {
      const op = next().v;
      left = { op, left, right: parseConcat() };
    }
    return left;
  }
  function parseConcat() {
    let left = parseAdd();
    while (peek().t === "op" && peek().v === "&") { next(); left = { op: "&", left, right: parseAdd() }; }
    return left;
  }
  function parseAdd() {
    let left = parseMul();
    while (peek().t === "op" && (peek().v === "+" || peek().v === "-")) {
      const op = next().v; left = { op, left, right: parseMul() };
    }
    return left;
  }
  function parseMul() {
    let left = parseUnary();
    while (peek().t === "op" && (peek().v === "*" || peek().v === "/" || peek().v === "%")) {
      const op = next().v; left = { op, left, right: parseUnary() };
    }
    return left;
  }
  function parseUnary() {
    if (peek().t === "op" && peek().v === "-") { next(); return { unop: "neg", arg: parseUnary() }; }
    return parsePrimary();
  }
  function parsePrimary() {
    const tk = peek();
    if (tk.t === "num") { next(); return { lit: tk.v }; }
    if (tk.t === "str") { next(); return { lit: tk.v }; }
    if (tk.t === "bool") { next(); return { lit: tk.v }; }
    if (tk.t === "col") { next(); return { col: tk.v }; }
    if (tk.t === "op" && tk.v === "(") { next(); const e = parseExpr(); expect(")"); return e; }
    if (tk.t === "ident") {
      next();
      const name = tk.v.toUpperCase();
      // 関数呼び出し
      let args = [];
      if (peek().t === "op" && peek().v === "(") {
        next();
        if (!(peek().t === "op" && peek().v === ")")) {
          args.push(parseExpr());
          while (peek().t === "op" && peek().v === ",") { next(); args.push(parseExpr()); }
        }
        expect(")");
      } else {
        throw new Error(`関数には () が必要です: ${tk.v}`);
      }
      // context 系はノード化
      if (name === "TODAY") return { ctx: "today" };
      if (name === "NOW") return { ctx: "now" };
      if (name === "USER" || name === "USEREMAIL") return { ctx: "currentUser" };
      return { fn: name, args };
    }
    throw new Error(`式が不正です（トークン: ${JSON.stringify(tk)}）`);
  }

  const ast = parseExpr();
  if (peek().t !== "eof") throw new Error("式の末尾に余分なトークンがあります");
  return ast;
}

// ---------------------------------------------------------------- evaluator
const WHITELIST = new Set([
  "IF", "AND", "OR", "NOT", "CONCATENATE", "CONCAT", "UPPER", "LOWER", "LEN", "TRIM",
  "CONTAINS", "ISBLANK", "COALESCE", "DATEDIFF", "ROUND", "ABS", "MIN", "MAX",
]);

const isBlank = (v) => v == null || v === "";
const truthy = (v) => !(v == null || v === false || v === 0 || v === "" || (typeof v === "number" && Number.isNaN(v)));
export { truthy };

function num(v) { const n = Number(v); if (Number.isNaN(n)) throw new Error(`数値が必要: ${v}`); return n; }
function str(v) { return v == null ? "" : String(v); }

function applyOp(op, a, b) {
  switch (op) {
    case "+": return num(a) + num(b);
    case "-": return num(a) - num(b);
    case "*": return num(a) * num(b);
    case "/": { const d = num(b); return d === 0 ? null : num(a) / d; }
    case "%": { const d = num(b); return d === 0 ? null : num(a) % d; }
    case "&": return str(a) + str(b);
    case "==": return a === b;
    case "!=": return a !== b;
    case ">": return a > b;
    case ">=": return a >= b;
    case "<": return a < b;
    case "<=": return a <= b;
    default: throw new Error(`未知の演算子: ${op}`);
  }
}

/**
 * AST を評価する。
 * @param {object} ast - Expression
 * @param {Record<string, unknown>} row
 * @param {{ userId?: string, now?: Date }} [ctx]
 */
export function evaluateExpression(ast, row = {}, ctx = {}) {
  const guard = { steps: 0 };
  const now = ctx.now ?? new Date();

  function ev(node, depth) {
    if (++guard.steps > MAX_STEPS) throw new Error("式が複雑すぎます（ステップ上限）");
    if (depth > MAX_DEPTH) throw new Error("式が深すぎます（深さ上限）");
    if (node == null || typeof node !== "object") throw new Error("不正なノード");

    if ("lit" in node) return node.lit;
    if ("col" in node) return row[node.col] ?? null;
    if ("ctx" in node) {
      if (node.ctx === "currentUser") return ctx.userId ?? null;
      if (node.ctx === "now") return now.toISOString();
      if (node.ctx === "today") return now.toISOString().slice(0, 10);
      throw new Error(`未知の context: ${node.ctx}`);
    }
    if ("unop" in node) {
      if (node.unop === "neg") return -num(ev(node.arg, depth + 1));
      if (node.unop === "not") return !truthy(ev(node.arg, depth + 1));
      throw new Error(`未知の単項: ${node.unop}`);
    }
    if ("op" in node) return applyOp(node.op, ev(node.left, depth + 1), ev(node.right, depth + 1));
    if ("fn" in node) return callFn(node.fn, node.args, depth);
    throw new Error("不明な式ノード");
  }

  function callFn(name, args, depth) {
    if (!WHITELIST.has(name)) throw new Error(`許可されていない関数: ${name}`); // ← サンドボックス境界
    // 短絡評価する関数
    if (name === "IF") return truthy(ev(args[0], depth + 1)) ? ev(args[1], depth + 1) : ev(args[2] ?? { lit: null }, depth + 1);
    if (name === "AND") { for (const a of args) if (!truthy(ev(a, depth + 1))) return false; return true; }
    if (name === "OR") { for (const a of args) if (truthy(ev(a, depth + 1))) return true; return false; }

    const a = args.map((x) => ev(x, depth + 1));
    switch (name) {
      case "NOT": return !truthy(a[0]);
      case "CONCATENATE":
      case "CONCAT": return a.map(str).join("");
      case "UPPER": return str(a[0]).toUpperCase();
      case "LOWER": return str(a[0]).toLowerCase();
      case "LEN": return str(a[0]).length;
      case "TRIM": return str(a[0]).trim();
      case "CONTAINS": return str(a[0]).includes(str(a[1]));
      case "ISBLANK": return isBlank(a[0]);
      case "COALESCE": return a.find((v) => !isBlank(v)) ?? null;
      case "DATEDIFF": {
        const d1 = new Date(a[0]); const d2 = new Date(a[1]);
        return Math.round((d1 - d2) / 86400000);
      }
      case "ROUND": { const d = a[1] == null ? 0 : num(a[1]); const f = 10 ** d; return Math.round(num(a[0]) * f) / f; }
      case "ABS": return Math.abs(num(a[0]));
      case "MIN": return Math.min(...a.map(num));
      case "MAX": return Math.max(...a.map(num));
      default: throw new Error(`未実装の関数: ${name}`);
    }
  }

  return ev(ast, 0);
}

/** テキスト式を直接評価するヘルパ（パース→評価）。 */
export function evalFormula(formula, row, ctx) {
  return evaluateExpression(parseFormula(formula), row, ctx);
}
